import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, FormControl, FormGroupDirective, UntypedFormGroup } from '@angular/forms';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { TimeApiService } from 'cbc-shared-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ShishinInputTab } from 'sg-api';
import { Sgke370dDialogComponent } from '../../../sgke370d/components/sgke370d-dialog/sgke370d-dialog.component';
import { Sgke370dDialogData } from '../../../sgke370d/models/sgke370d-dialog-data';

@Component({
  selector: 'app-sgke360d-guidingvalue-input',
  templateUrl: './sgke360d-guidingvalue-input.component.html',
  styleUrl: './sgke360d-guidingvalue-input.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class Sgke360dGuidingvalueInputComponent implements OnInit, OnDestroy {
  /** 指針入力タブ内のレスポンス */
  @Input() shishinInputTab: ShishinInputTab = null;

  /** モーダル表示状況エミッター */
  @Output() onModalIsDisplayEvent = new EventEmitter();

  /** 親フォームグループ */
  public parentForm: UntypedFormGroup;

  /** MatDialog戻り値 */
  ref: MatDialogRef<Sgke370dDialogComponent, any>;

  /** 購読を停止するためのSubject */
  private onDestroy$ = new Subject();

  /** 指針ボタンの無効取得 */
  public get isDisableShishinBtn() {
    // 一般供給再開
    const isIppan = this.getControl('workClscode')?.value == '2';

    // 立会人
    const taiper = this.getControl('supplyresumptionTaiperDivcd');
    // 周知不可
    const witness = this.getControl('witnessNotnoticeFlg');
    // 立会人未入力または周知不可の場合
    return isIppan && (!taiper.value || (taiper.value == '90' && witness.value == '1'));
  }

  /**
   * コンストラクタ
   * @param {FormGroupDirective} parent
   * @param {MatDialog} dialog
   * @memberof Sgke360dGuidingvalueInputComponent
   */
  constructor(
    public timeApiService: TimeApiService,
    private parent: FormGroupDirective,
    private dialog: MatDialog,
  ) {
  }
  /**
   * 初期化処理
   *
   * @memberof Sgke360dGuidingvalueInputComponent
   */
  ngOnInit(): void {
    // 親フォームを参照
    this.parentForm = this.parent.form.get('supplyresumptionResultInfo') as UntypedFormGroup;
  }
  /**
   * 終了処理
   *
   * @memberof Sgke360dGuidingvalueInputComponent
   */
  ngOnDestroy(): void {
    this.parentForm.clearValidators();
    this.parentForm.updateValueAndValidity();
    //購読を停止する処理
    this.onDestroy$.next();
  }

  /**
   * 指針入力ボタン押下時処理
   *
   * @memberof Sgke360dGuidingvalueInputComponent
   */
  public onClickShishinBtn(): void {
    const data: Sgke370dDialogData = {
      suppntSpno: this.parentForm.controls['suppntSpno'].value,
      supplystopresumptionRrkno: this.parentForm.controls['supplystopresumptionRrkno'].value,
      supplystopresumptionWorkClscode: this.parentForm.controls['supplystopresumptionWorkClscode'].value,
      energyClscode: this.parentForm.controls['energyClscode'].value,
      supplystopYearmonthdate: this.timeApiService.convertDateToString(this.parentForm.controls['supplystopYearmonthdate'].value),
      kenshinTiming: '供給停止時',
      preMeterNo: this.shishinInputTab.preMeterNo,
      preShishin: this.shishinInputTab.duringstopShishin,
      preMeterModel: this.shishinInputTab.preMeterModel,
      preMeterAbility: this.shishinInputTab.preMeterAbility,
      meterInfoIcchiflag: this.parentForm.controls['meterInfoIcchiflag'].value,
      meterTorikaeEonflg: this.parentForm.controls['meterTorikaeEonflg'].value,
      removalMeterNo: this.parentForm.controls['removalMeterNo'].value,
      duringremovalShishin: this.parentForm.controls['duringremovalShishin'].value,
      installationMeterNo: this.parentForm.controls['installationMeterNo'].value,
      duringinstallationShishin: this.parentForm.controls['duringinstallationShishin'].value,
      meterRotationEonflg: this.parentForm.controls['meterRotationEonflg'].value,
      duringstopShishin: this.getControl('onresumptionShishin').value ?? this.shishinInputTab.duringstopShishin,
    };

    this.onModalIsDisplayEvent.emit(false);
    // ダイアログオープン
    this.ref = this.dialog.open(Sgke370dDialogComponent, {
      height: '950px',
      width: '1000px',
      disableClose: true,
      data: data,
    });

    // ダイアログクローズ時の処理
    this.ref.afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(result => {
        this.onModalIsDisplayEvent.emit(true);
        const propMap = {
          meterInfoIcchiflag: 'meterInfoIcchiflag',
          meterTorikaeEonflg: 'meterTorikaeEonflg',
          removalMeterNo: 'removalMeterNo',
          duringremovalShishin: 'duringremovalShishin',
          installationMeterNo: 'installationMeterNo',
          duringinstallationShishin: 'duringinstallationShishin',
          meterRotationEonflg: 'meterRotationEonflg',
          duringstopShishin: 'onresumptionShishin',
          elecCsp: '',
          resultregistrationFinDisableflag: 'resultregistrationFinDisableflag',
        };
        Object.entries(result).forEach(([key, val]) => {
          const value = val === '' ? null : val;
          this.getControl(propMap[key])?.setValue(value);
          if (key == 'installationMeterNo') {
            // 取付メータ番号が入力された場合は、再開時メーター番号を置き換え
            // それ以外は、前回メータ番号
            var currentMeterNo  = val ?? this.shishinInputTab.preMeterNo;
            this.getControl('onresumptionMeterNo')?.setValue(currentMeterNo);
          }
        });
      });
  }

  /**
   * フォームコントロール取得
   * @param ctlname コントロール名
   * @returns フォームコントロール
   */
  getControl(ctlname: string): FormControl {
    return this.parentForm?.get(ctlname) as FormControl;
  }
}
